import React, { useState, useEffect } from "react";
import Logo from "../Pngs/AS text logo.png";
import { FaBars, FaTimes, FaGithub, FaLinkedin, FaTerminal } from "react-icons/fa";
import { HiOutlineMail as MailIcon } from "react-icons/hi";
import { BsFillPersonLinesFill } from "react-icons/bs";
import { Link } from "react-scroll";
import { motion, AnimatePresence } from "framer-motion";

interface NavbarProps {
  onOpenAdmin: () => void;
  onOpenResume: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenAdmin, onOpenResume }) => {
  const [nav, setNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleClick = () => setNav(!nav);

  // Monitor scroll to shrink header and add glassmorphism shadow
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Header Navbar */}
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`fixed w-full left-0 top-0 transition-all duration-300 flex justify-between items-center px-6 md:px-12 z-40 ${
          scrolled
            ? "h-[64px] bg-cyber-darker/80 backdrop-blur-xl border-b border-cyber-cyan/15 shadow-glow-cyan-sm"
            : "h-[80px] bg-transparent"
        }`}
      >
        <Link to="home" smooth={true} duration={500} className="cursor-pointer">
          <div className="flex items-center space-x-2 group">
            <img
              src={Logo}
              alt="Alok Singh"
              className="w-[40px] border border-cyber-cyan/30 rounded-lg group-hover:border-cyber-cyan group-hover:shadow-glow-cyan-sm transition-all duration-300"
            />
            <span className="font-mono text-xs text-cyber-cyan hidden sm:inline-block tracking-widest opacity-80 group-hover:opacity-100 transition duration-300">
              ALOK_SINGH // PORTFOLIO
            </span>
          </div>
        </Link>

        {/* Desktop Menu */}
        <ul className="hidden md:flex items-center font-mono text-sm space-x-2">
          {["home", "about", "skills", "project", "contact"].map((section) => (
            <li key={section}>
              <Link
                activeClass="text-cyber-cyan font-bold"
                spy={true}
                className="px-4 py-2 hover:text-cyber-cyan transition-colors duration-200 cursor-pointer uppercase text-xs tracking-wider"
                to={section}
                smooth={true}
                duration={500}
              >
                {section}
              </Link>
            </li>
          ))}
          <li>
            <button
              onClick={onOpenAdmin}
              className="flex items-center space-x-1.5 px-3 py-1.5 border border-cyber-purple/40 text-cyber-purple hover:bg-cyber-purple/10 hover:border-cyber-purple hover:shadow-glow-purple-sm rounded font-bold text-xs tracking-wider transition-all duration-300 ml-4"
            >
              <FaTerminal className="w-3 h-3" />
              <span>TERMINAL</span>
            </button>
          </li>
        </ul>

        {/* Mobile Hamburger */}
        <div
          onClick={handleClick}
          className="md:hidden z-50 cursor-pointer text-cyber-cyan hover:text-white transition duration-200"
        >
          {!nav ? <FaBars size={20} /> : <FaTimes size={20} />}
        </div>
      </motion.div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {nav && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed inset-0 w-full h-screen bg-cyber-black/95 backdrop-blur-2xl flex flex-col justify-center items-center font-mono space-y-6 text-xl z-30"
          >
            {["home", "about", "skills", "project", "contact"].map((section) => (
              <Link
                key={section}
                onClick={handleClick}
                to={section}
                smooth={true}
                duration={500}
                className="hover:text-cyber-cyan cursor-pointer uppercase tracking-widest text-lg"
              >
                {section}
              </Link>
            ))}
            <button
              onClick={() => {
                handleClick();
                onOpenAdmin();
              }}
              className="flex items-center space-x-2 px-5 py-2.5 border border-cyber-purple text-cyber-purple hover:bg-cyber-purple/15 rounded text-base font-bold transition duration-300 mt-4"
            >
              <FaTerminal />
              <span>OPEN TERMINAL</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Social Sidebar - Left Floating Bar (Always Visible, z-index 50 to avoid cover-ups) */}
      <div className="fixed bottom-8 left-4 lg:bottom-auto lg:top-[35%] lg:left-0 flex flex-row lg:flex-col gap-3 z-50">
        {[
          {
            name: "LinkedIn",
            href: "https://www.linkedin.com/in/aloksingh9/",
            icon: <FaLinkedin size={20} />,
            bg: "bg-[#0A66C2]/95 border-cyber-cyan/50",
          },
          {
            name: "GitHub",
            href: "https://github.com/aloki9singh",
            icon: <FaGithub size={20} />,
            bg: "bg-[#181B24]/95 border-cyber-cyan/30",
          },
          {
            name: "Email",
            href: "mailto:kshatriyaalok1997@gmail.com",
            icon: <MailIcon size={20} />,
            bg: "bg-[#A855F7]/95 border-cyber-purple/50",
          },
          {
            name: "Resume",
            href: "./resume.html",
            icon: <BsFillPersonLinesFill size={20} />,
            bg: "bg-[#334155]/95 border-cyber-cyan/30",
          },
        ].map((item, idx) => (
          <motion.a
            key={idx}
            href={item.href}
            onClick={(e) => {
              if (item.name === "Resume") {
                e.preventDefault();
                onOpenResume();
              }
            }}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1, x: 5 }}
            className={`flex items-center justify-center w-10 h-10 lg:w-[120px] lg:h-12 lg:ml-[-80px] lg:hover:ml-[-10px] rounded-full lg:rounded-none lg:rounded-r-lg border-r-2 text-white transition-all duration-300 ${item.bg} shadow-lg shadow-black/40`}
          >
            <span className="hidden lg:inline-block font-mono text-xs font-semibold mr-3 w-16 text-left">
              {item.name}
            </span>
            <div className="flex-shrink-0">{item.icon}</div>
          </motion.a>
        ))}
      </div>
    </>
  );
};
